<script setup lang="ts">
import { IDataTablePaginationData } from "@js/types/default";
import { computed } from "vue";

const props = withDefaults(
	defineProps<{
		paginationData: IDataTablePaginationData;
		itemsPerPage: number;
		currentPage: number;
		hideItemsPerPage: boolean;
	}>(),
	{},
);

const paginationInfo = computed(() => {
	const startIndex = (props.currentPage - 1) * props.itemsPerPage + 1;
	const endIndex = Math.min(
		props.currentPage * props.itemsPerPage,
		props.paginationData.totalCount,
	);

	return { startIndex, endIndex };
});

const totalPages = computed(() =>
	Math.ceil(props.paginationData.totalCount / props.itemsPerPage),
);

const pageRange = computed(() => {
	const range: number[] = [];
	const start = Math.max(1, props.currentPage - 1);
	const end = Math.min(totalPages.value, props.currentPage + 1);

	for (let i = start; i <= end; i++) {
		range.push(i);
	}

	if (range.length < 3) {
		if (start > 1) {
			range.unshift(start - 1);
		} else if (end < totalPages.value) {
			range.push(end + 1);
		}
	}

	return range;
});

const emit = defineEmits<{
	handleItemsPerPage: [items: number];
	handleCurrentPage: [page: number];
}>();
</script>

<template>
	<div
		class="datatable-footer d-flex align-items-center justify-content-between mt-3"
	>
		<p class="small fw-medium mb-0">
			{{ paginationInfo.startIndex }}-{{ paginationInfo.endIndex }} of
			{{ paginationData.totalCount }}
		</p>

		<div class="d-flex align-items-center justify-content-between gap-1">
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary"
				:disabled="currentPage <= 1"
				@click="emit('handleCurrentPage', currentPage - 1)"
			>
				&#60;
			</button>

			<button
				v-for="page in pageRange"
				:key="page"
				type="button"
				class="btn btn-sm"
				:class="currentPage === page ? 'btn-primary' : 'btn-outline-secondary'"
				@click="emit('handleCurrentPage', page)"
			>
				{{ page }}
			</button>

			<button
				type="button"
				class="btn btn-sm btn-outline-secondary"
				:disabled="currentPage === totalPages"
				@click="emit('handleCurrentPage', currentPage + 1)"
			>
				&#62;
			</button>
		</div>
	</div>
</template>
